import React, { useCallback, useEffect, useRef, useState } from 'react';
import OrderSearchAndSort from './OrderSearchAndSort';

/**
 * THE phone search affordance: a 🔍 toggle that opens a search row UNDER the
 * header (the results stay visible below it — never a search box inside a
 * sheet or menu that covers them; owner 2026-10-28, timesheets). Extracted
 * 2026-10-28 from `MobileTabSearchRow.js` (unchanged behaviour) so the
 * timesheets phone header (`timesheets/PhoneContextBar.js`) shares it.
 *
 * 🔑 The row is open while the user opened it OR while a term is active, so a
 * live filter can never sit hidden behind a closed row. ✕ clears AND closes.
 * The magnifier: closed → open + focus; open with a term → refocus; open and
 * empty → close. The caller keeps the search STATE and filters by it.
 */
export function usePhoneSearch({ searchText, onSearchChange }) {
  const [open, setOpen] = useState(false);
  const rowRef = useRef(null);
  const searchActive = String(searchText || '').trim() !== '';
  const rowOpen = open || searchActive;
  const focusInput = useCallback(() => {
    const input = rowRef.current && rowRef.current.querySelector('input');
    if (input) input.focus();
  }, []);
  const onIconClick = useCallback(() => {
    if (!rowOpen) setOpen(true);
    else if (searchActive) focusInput();
    else setOpen(false);
  }, [rowOpen, searchActive, focusInput]);
  const onClose = useCallback(() => {
    onSearchChange('');
    setOpen(false);
  }, [onSearchChange]);
  // Focus the input when the row OPENS (not on every render while open).
  const prevOpen = useRef(rowOpen);
  useEffect(() => {
    if (rowOpen && !prevOpen.current) focusInput();
    prevOpen.current = rowOpen;
  }, [rowOpen, focusInput]);
  return { rowOpen, searchActive, rowRef, onIconClick, onClose };
}

/** The 🔍 toggle. Testid `${idPrefix}-search-toggle`. */
export function PhoneSearchToggle({ idPrefix, search, label }) {
  return (
    <button
      type="button"
      onClick={search.onIconClick}
      aria-label={label}
      aria-expanded={search.rowOpen}
      aria-controls={`${idPrefix}-mobile-search-row`}
      aria-pressed={search.searchActive}
      data-testid={`${idPrefix}-search-toggle`}
      data-search-active={search.searchActive ? 'true' : undefined}
      className={`shrink-0 inline-flex items-center justify-center h-11 w-11 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
        search.searchActive
          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
          : 'text-gray-600 hover:bg-white dark:text-gray-300 dark:hover:bg-gray-700'
      }`}
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    </button>
  );
}

/** The search row (renders nothing while closed). Testids `${idPrefix}-mobile-search-row`, `-search-close`. */
export function PhoneSearchRow({
  idPrefix, search, searchText, onSearchChange, searchPlaceholder, searchAriaLabel, debounceMs, searchTestIdPrefix,
}) {
  if (!search.rowOpen) return null;
  const rowId = `${idPrefix}-mobile-search-row`;
  return (
    <div
      ref={search.rowRef}
      id={rowId}
      /* The browser's own type=search ✕ is suppressed HERE ONLY: beside
         the row's ✕ it was a second, different "clear" (it clears but
         leaves the row open). One clear control, the one asked for. */
      className="flex items-center w-full gap-1 [&_input::-webkit-search-cancel-button]:appearance-none"
      data-testid={rowId}
    >
      <div className="flex-1 min-w-0">
        <OrderSearchAndSort
          searchText={searchText}
          onSearchChange={onSearchChange}
          searchPlaceholder={searchPlaceholder}
          searchAriaLabel={searchAriaLabel}
          debounceMs={debounceMs}
          testIdPrefix={searchTestIdPrefix}
        />
      </div>
      <button
        type="button"
        onClick={search.onClose}
        aria-label="Clear search and close"
        data-testid={`${idPrefix}-search-close`}
        className="shrink-0 inline-flex items-center justify-center h-11 w-11 rounded-lg text-gray-600 hover:bg-white dark:text-gray-300 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <span aria-hidden="true" className="text-lg leading-none">✕</span>
      </button>
    </div>
  );
}
